import React, { useEffect, useRef, useMemo } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import * as d3 from "d3";
import { ResizableTable } from "../../../components/resizableTable/ResizableTable";
import DataVisualizer from "../../../components/ChartBuilder/DataVisualizer";

// Fallback color palette
const renewalsColorPalette = ["#548235", "#A9D18E", "#FF0000", "#4473C4", "#ED7C31", "#4E79A7", "#F28E2B"];
const getColorScale = (chartDataConfig, statuses) => {
  const configList = chartDataConfig?.renewalStatusConfig || [];
  if (configList.length > 0) {
    const colorMap = {};
    configList.forEach(item => {
      colorMap[item.Renewal_Status] = item.Color_Hex_Code;
    });
    return (status) => colorMap[status] || "#cccccc";
  }
  return d3.scaleOrdinal(renewalsColorPalette).domain(statuses);
};

// Main Component
const RenewalsSummaryPaper = ({ classes, paperTitle, currencySymbol, renewalsSummaryData, isRenewalsSummaryLoading, handleRenewalsSummaryDrilldown }) => {
  const chartDataConfig = useMemo(() => renewalsSummaryData || {}, [renewalsSummaryData]);
  const data = useMemo(() => chartDataConfig.data || [], [chartDataConfig]);
  // Create a direct stage-to-color mapping for DataVisualizer override
  const RenewalStatusColors = useMemo(() => {
    const config = chartDataConfig.renewalStatusConfig;
    if (!config || config.length === 0) return null;
    const colorMap = {};
    config.forEach(item => {
      colorMap[item.Renewal_Status] = item.Color_Hex_Code;
    });
    return colorMap;
  }, [chartDataConfig]);

  const userSortedStages = useMemo(() => chartDataConfig.renewalStatusConfig?.map(c => c.Renewal_Status) || [], [chartDataConfig]);
  const userColors = useMemo(() => chartDataConfig.renewalStatusConfig?.map(c => c.Color_Hex_Code) || renewalsColorPalette, [chartDataConfig]);

  const legendItems = useMemo(() => {
    const config = chartDataConfig.renewalStatusConfig || [];
    return [...config]
      .sort((a, b) => a.Sequence - b.Sequence)
      .map(c => ({ text: c.Renewal_Status, color: c.Color_Hex_Code }));
  }, [chartDataConfig]);

  const rawColumns = useMemo(() => {
    const quarters = chartDataConfig.renewalDueDateFiscalQuarter || [];
    
    let baseCols = [
      {
        label: "Renewal Due Date Fiscal Quarter",
        subColumns: [
          {
            label: "Renewal Status",
            field: "renewalStatus",
            width: 150,
            align: "left",
            pinned: "left",
            style: { cursor: "default" },
            sortable: true,
            filterable: true,
          }
        ],
        headerBgColor: "#ffffff",
        headerTextColor: "#000000"
      }
    ];

    quarters.forEach((q, idx) => {
      // Alternate header colors
      const isAlt = idx % 2 !== 0;
      baseCols.push({
        label: q,
        subColumns: [
          {
            label: "# of Opps",
            field: `${q}_count`,
            type: "number",
            width: 35,
            align: "right",
            style: { cursor: handleRenewalsSummaryDrilldown ? "pointer" : "default" },
            onClick: (row) => {
              if (handleRenewalsSummaryDrilldown) {
                handleRenewalsSummaryDrilldown({ type: 'table', quarter: q, status: row.renewalStatus });
              }
            },
            headerBgColor: "#ffffff",
            headerTextColor: "#333333",
            sortable: true,
            filterable: true,
          },
          {
            label: `ACV (${currencySymbol}K)`,
            field: `${q}_value`,
            type: "number",
            width: 140,
            align: "right",
            style: { cursor: handleRenewalsSummaryDrilldown ? "pointer" : "default" },
            onClick: (row) => {
              if (handleRenewalsSummaryDrilldown) {
                handleRenewalsSummaryDrilldown({ type: 'table', quarter: q, status: row.renewalStatus });
              }
            },
            headerBgColor: "#ffffff",
            headerTextColor: "#333333",
            sortable: true,
            filterable: true,
          },
          {
            label: "% of Total",
            field: `${q}_pct`,
            type: "percentage",
            width: 35,
            align: "right",
            style: { cursor: handleRenewalsSummaryDrilldown ? "pointer" : "default" },
            onClick: (row) => {
              if (handleRenewalsSummaryDrilldown) {
                handleRenewalsSummaryDrilldown({ type: 'table', quarter: q, status: row.renewalStatus });
              }
            },
            headerBgColor: "#ffffff",
            headerTextColor: "#333333",
            sortable: true,
            filterable: true,
          }
        ],
        headerBgColor: isAlt ? "#5B9BD5" : "#4471C4",
        headerTextColor: "#ffffff"
      });
    });

    const isAltForTotal = quarters.length % 2 !== 0;
    baseCols.push({
      label: "Total",
      subColumns: [
        {
          label: "# of Opps",
          field: `Total_count`,
          type: "number",
          width: 35,
          align: "right",
          style: { cursor: handleRenewalsSummaryDrilldown ? "pointer" : "default" },
          onClick: (row) => {
            if (handleRenewalsSummaryDrilldown) {
              handleRenewalsSummaryDrilldown({ type: 'table', quarter: 'Total', status: row.renewalStatus });
            }
          },
          headerBgColor: "#ffffff",
          headerTextColor: "#333333",
          sortable: true,
          filterable: true,
        },
        {
          label: `ACV (${currencySymbol}K)`,
          field: `Total_value`,
          type: "number",
          width: 140,
          align: "right",
          style: { cursor: handleRenewalsSummaryDrilldown ? "pointer" : "default" },
          onClick: (row) => {
            if (handleRenewalsSummaryDrilldown) {
              handleRenewalsSummaryDrilldown({ type: 'table', quarter: 'Total', status: row.renewalStatus });
            }
          },
          headerBgColor: "#ffffff",
          headerTextColor: "#333333",
          sortable: true,
          filterable: true,
        },
        {
          label: "% of Total",
          field: `Total_pct`,
          type: "percentage",
          width: 35,
          align: "right",
          style: { cursor: handleRenewalsSummaryDrilldown ? "pointer" : "default" },
          onClick: (row) => {
            if (handleRenewalsSummaryDrilldown) {
              handleRenewalsSummaryDrilldown({ type: 'table', quarter: 'Total', status: row.renewalStatus });
            }
          },
          headerBgColor: "#ffffff",
          headerTextColor: "#333333",
          sortable: true,
          filterable: true,
        }
      ],
      headerBgColor: isAltForTotal ? "#5B9BD5" : "#4471C4",
      headerTextColor: "#ffffff"
    });

    return baseCols;
  }, [chartDataConfig]);

  const tableRows = useMemo(() => {
    const quarters = chartDataConfig.renewalDueDateFiscalQuarter || [];
    const statuses = chartDataConfig.renewalStatusConfig?.map(c => c.Renewal_Status) || [];
    
    if (quarters.length === 0 || statuses.length === 0) return [];

    let rows = [];

    const totalRowData = chartDataConfig.totalRow || [];
    const totalColumnData = chartDataConfig.totalColumn || [];

    statuses.forEach(s => {
      let row = { renewalStatus: s, isStatusRow: true };

      quarters.forEach(q => {
        const item = data.find(d => d.Renewal_Due_Date_Fiscal_Quarter === q && d.Renewal_Status === s);
        row[`${q}_value`] = item ? (item.Acv || 0) : 0;
        row[`${q}_count`] = item ? (item.Count || 0) : 0;
        row[`${q}_pct`] = item ? Math.round((item.totalPercentage || 0) * 100) : 0;
      });

      // Total for the row (from totalColumn array)
      const tColItem = totalColumnData.find(d => d.RenewalStatus === s);
      row.Total_value = tColItem ? tColItem.totalAcv : 0;
      row.Total_count = tColItem ? tColItem.totalCount : 0;
      row.Total_pct = tColItem ? Math.round((tColItem.totalPer || 0) * 100) : 0;

      rows.push(row);
    });

    // Total Row
    let totalRowObj = { renewalStatus: "Total", isTotalRow: true, isTotal: true };
    quarters.forEach(q => {
       const tRowItem = totalRowData.find(d => d.Renewal_Due_Date_Fiscal_Quarter === q);
       totalRowObj[`${q}_value`] = tRowItem ? tRowItem.totalAcv : 0;
       totalRowObj[`${q}_count`] = tRowItem ? tRowItem.totalCount : 0;
       totalRowObj[`${q}_pct`] = tRowItem ? Math.round((tRowItem.totalPer || 0) * 100) : 0;
    });

    // Total of Total
    const grandTotalItem = totalRowData.find(d => d.Renewal_Due_Date_Fiscal_Quarter === "Total");
    totalRowObj.Total_value = grandTotalItem ? grandTotalItem.totalAcv : 0;
    totalRowObj.Total_count = grandTotalItem ? grandTotalItem.totalCount : 0;
    totalRowObj.Total_pct = grandTotalItem ? Math.round((grandTotalItem.totalPer || 0) * 100) : 0;

    rows.push(totalRowObj);

    // Sort by ACV Total (Total_value) descending, keep Total row last
    const totalRow = rows.pop();
    rows.sort((a, b) => {
      const aVal = a.Total_value != null ? a.Total_value : 0;
      const bVal = b.Total_value != null ? b.Total_value : 0;
      return bVal - aVal;
    });
    rows.push(totalRow);

    return rows;
  }, [data, chartDataConfig]);

  return (
    <Grid item xs={12}>
      <Paper className={classes.paper}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          <Typography variant="h5" className={classes.title}>
            {chartDataConfig.title || "Renewals Summary"}
          </Typography>
          <span className={classes.updateFrequency}>{"Live Data"}</span>
          <Typography style={{ marginTop: 4 ,fontWeight: "bold",fontSize: "0.875rem" }}>
            (in {currencySymbol}K)
          </Typography>
        </Box>

        {isRenewalsSummaryLoading ? (
            <Skeleton variant="rect" animation="wave" height={400} width="100%" />
        ) : data.length === 0 ? (
            <Box textAlign="center" py={4} style={{ fontStyle: "italic", color: "#666" }}>
                No renewals summary data available
            </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              <DataVisualizer
                chartName="Renewals Summary"
                title=""
                type="Renewal_Status"
                chartType="acv"
                Value_Label="ACV"
                winACVMixAnalysisData={data.map(d => ({
                  ...d,
                  acv: d.Acv,
                  deals: d.Count
                }))}
                clusterTotal={[]}
                tableComponent={<></>}
                quarter="Renewal_Due_Date_Fiscal_Quarter"
                unit="acv"
                sortByTotal={false}
                label="Renewals"
                handleChangeLocation={(location, data, type, quarterProp, stageType) => {
                  if (handleRenewalsSummaryDrilldown) {
                    let status = "Total";
                    let quarter = "Total";

                    if (data && typeof data === "object") {
                        if (data.row) {
                           status = data.row;
                        }
                        if (data.cell && data.cell !== "Total") {
                           quarter = data.cell;
                        }
                    }

                    handleRenewalsSummaryDrilldown({
                       type: 'chart',
                       quarter: quarter,
                       status: status
                    });
                  }
                }}
                toggleValue="AcvSort"
                handleChangeToggle={() => {}}
                disableCard={true}
                sortedStages={userSortedStages}
                colors={userColors}
                renewalStatusColors={RenewalStatusColors}
                legendOverride={legendItems}
                UICols={[
                  { col: "deals", label: "# of Opps", type: "number" },
                  { col: "acv", label: "ACV", type: "currency" },
                  { col: null, label: "% of Total", type: "perc" }
                ]}
              />
            </Grid>

            <Box mt={4} mb={2}>
              <ResizableTable
                  columns={rawColumns}
                  rows={tableRows}
                  stickyFirstColumn={true}
                  spanFirstColumn={false}
                  getCellValue={(row, field) => {
                    if (field.endsWith('_value')) {
                      return d3.format(",.1f")((row[field] || 0) / 1000);
                    }
                    if (field.endsWith('_pct')) {
                      return `${row[field]}`;
                    }
                    return row[field] != null ? row[field] : "";
                  }}
                  getCellStyle={(row, field) => {
                    const style = {};
                    if (row.isTotalRow) {
                      style.fontWeight = "bold";
                      style.backgroundColor = "#f5f5f5";
                    }
                    if (field === "renewalStatus" && row.isStatusRow) {
                      style.paddingLeft = "24px";
                    }
                    return style;
                  }}
                  rowKey="renewalStatus"
                  hideSubHeaderRow={false}
                  maxWidth="100%"
                />
            </Box>
          </>
        )}
      </Paper>
    </Grid>
  );
};

export default RenewalsSummaryPaper;

